/* eslint-disable @next/next/no-img-element */
import { useEffect, useState } from "react";
import {
  ArchetypeDiscovery,
  TraitIdentifier,
} from "@dimensional-engineering/dimensional-models";
import { useSelector } from "react-redux";
import { Roboto_Mono } from "next/font/google";
import { RootState } from "@/redux/store";

import styles from "./ArchetypeDiscovery.module.css";

import archetypeJsonData from "../../../../../assets/traitInfo/archetypes.json";

import { ExtendedArchetype } from "@/models/sharedModels";
import Archetype from "@/components/traits/arcehtype/Archetypes/Archetype";
import ArchetypeUnknown from "@/components/traits/arcehtype/Archetypes/ArchetypeUnknown";
import { useModalContext } from "@/context/ModalContext";
import ArchetypeModal from "@/components/traits/TraitsModals/Archetype/ArchetypeModal";
import { archetypeMapper } from "@/assets/traitInfo/dimensionData";
import { useStaggeredOpacity } from "@/helpers/useStaggeredOpacity";
import { getElementFromTraitIdentifier } from "@/components/authenticatedProfile/tabs/AuthCompareTab/utils";
import { getLengthOfLongestArchetypeName } from "@/components/assessments-new/assessmentUtils";

const font = Roboto_Mono({ subsets: ["latin"] });

type Props = {
  archetype: ArchetypeDiscovery | null;
};

type DeltaProps = {
  archetypeDeltaData?:
    | {
        previousTraitIdentifier: TraitIdentifier;
        currentTraitIdentifier: TraitIdentifier;
      }
    | null
    | undefined;
};

const descriptionMapper = {
  identity:
    "Cognition Archetypes represent how we perceive the world and process information.",
  "personality-II": "Identity",
  "love-style":
    "Love Archetypes represent how we conceptualize love, integrate partners into our lives, and evaluate romantic stress.",
  work: "Work Archetypes represent how we behave in work: what motivates us and how we communicate.",
  career:
    "Career Archetypes represent your interests and how they translate into career choice.",
  values:
    "Values Archetypes represent your primary motivations: the lens through which you make your big decisions.",
  love: "Love Archetypes represent how we conceptualize love, integrate partners into our lives, and evaluate romantic stress.",
};

export function ArchetypeDiscoveryScreenStepOne(props: Props) {
  const opacity = useStaggeredOpacity(4);

  return (
    <div className={styles.main}>
      <p style={{ opacity: opacity.step1 }} className={styles.archetypeText}>
        ARCHETYPE
      </p>
      <div style={{ opacity: opacity.step2 }} className={styles.archDiv}>
        <div className={styles.architypeBody}>
          <p className={styles.topText}>
            {props.archetype && archetypeMapper[props.archetype.dimensionSlug]}
          </p>

          <svg
            width="90"
            height="78"
            viewBox="0 0 90 78"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <clipPath id="ArchetypeImageClipPathLarge">
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M84.1172 77.6604C88.2137 77.6604 90.7568 73.1894 88.6744 69.6485L49.5568 3.1302C47.509 -0.351996 42.4902 -0.351994 40.4424 3.13021L1.32479 69.6485C-0.757529 73.1894 1.78558 77.6604 5.882 77.6604H84.1172ZM45.072 54.8056C48.6339 54.8056 51.5215 51.9073 51.5215 48.3319C51.5215 44.7566 48.6339 41.8582 45.072 41.8582C41.51 41.8582 38.6225 44.7566 38.6225 48.3319C38.6225 51.9073 41.51 54.8056 45.072 54.8056Z"
                fill="#FFFFF2"
              />
            </clipPath>
          </svg>

          <img
            alt="image"
            className="archetype-image-discovery"
            src={`/archetypes/${props.archetype?.archetype.slug}.jpg`}
            width="89px"
            height="89px"
          />
          <div className={styles.bottomTextDiv}>
            <p className={styles.bottomText}>
              {props.archetype?.archetype.archetypeGroupSlug !== "work"
                ? props.archetype?.archetype.acronym ??
                  props.archetype?.archetype.name
                : `${props.archetype.archetype.name} (${props.archetype.archetype.acronym})`}
            </p>
          </div>
        </div>
      </div>
      <div
        style={{ opacity: opacity.step3 }}
        className={styles.bottomStepOneDiv}
      >
        <p className={styles.yourDiscoveredText}>
          You discovered your{" "}
          {props.archetype &&
            archetypeMapper[props.archetype.archetype.archetypeGroupSlug]}{" "}
          Archetype
        </p>
        <p className={styles.descriptor}>
          {
            descriptionMapper[
              props.archetype?.archetype
                .archetypeGroupSlug as keyof typeof descriptionMapper
            ]
          }
        </p>
      </div>
    </div>
  );
}

export function ArchetypeDiscoveryScreenStepTwo(props: Props) {
  const [archetypeList, setArchetypeList] = useState<ExtendedArchetype[]>([]);

  const { setModalComponent } = useModalContext();

  const opacity = useStaggeredOpacity(2);

  useEffect(() => {
    setArchetypeList([]);
    archetypeJsonData.forEach((arch) => {
      if (
        arch.archetypeGroupSlug === props.archetype?.dimensionSlug &&
        arch.isDiscoverable === true &&
        props.archetype.dimensionSlug !== "spirit" &&
        props.archetype.dimensionSlug !== "values"
      ) {
        setArchetypeList((current) => [
          ...current,
          arch as unknown as ExtendedArchetype,
        ]);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.archetype?.archetypeSlug]);

  return (
    <div className={styles.main}>
      <p style={{ flex: "0 1 auto" }} className={styles.archetypeText}>
        ARCHETYPE
      </p>
      <div
        style={{ opacity: opacity.step1 }}
        className={styles.elementsWrapper}
      >
        <div className={styles.elementsDiv}>
          {archetypeList.map((arch, index) => {
            return (
              <div
                onClick={(e) => {
                  e.stopPropagation();
                  setModalComponent(<ArchetypeModal slug={arch.slug} />);
                }}
                key={arch.slug}
                className="elBodyNoMarginMobile"
                style={{ cursor: "pointer" }}
              >
                <Archetype maxWidth archetype={arch} />
              </div>
            );
          })}
        </div>
      </div>
      <p
        style={{ marginTop: "30px", opacity: opacity.step2 }}
        className={styles.yourDiscoveredText}
      >
        {props.archetype?.archetype.name} is one of {archetypeList.length}{" "}
        {props.archetype &&
          archetypeMapper[props.archetype.archetype.archetypeGroupSlug]}{" "}
        Archetypes
      </p>
    </div>
  );
}

export function ArchetypeDiscoveryStepThree(props: Props) {
  const { setModalComponent } = useModalContext();

  const opacity = useStaggeredOpacity(3);

  const [leftOutArchs, setLeftOutArchs] = useState<string[]>([]);

  const archetypesDiscovered = useSelector(
    (state: RootState) => state.user.archetypesDiscovered
  );

  useEffect(() => {
    let has = [];
    let leftOut = [];
    if (archetypesDiscovered) {
      for (let i = 0; i < archetypesDiscovered?.length; i++) {
        has.push(archetypesDiscovered[i].dimensionSlug);
      }
    }

    // if (!has.includes("values")) {
    //   leftOut.push("values");
    // }
    if (!has.includes("identity")) {
      leftOut.push("identity");
    }
    if (!has.includes("love")) {
      leftOut.push("love");
    }
    if (!has.includes("work")) {
      leftOut.push("interaction");
    }
    if (!has.includes("career")) {
      leftOut.push("career");
    }

    setLeftOutArchs(leftOut);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [archetypesDiscovered?.length]);

  const mapped = archetypesDiscovered?.map((arch, index) => {
    return (
      <div
        className={styles.arch}
        key={index}
        onClick={(e) => {
          e.stopPropagation();
          setModalComponent(<ArchetypeModal slug={arch.archetypeSlug} />);
        }}
        style={{ cursor: "pointer" }}
      >
        <Archetype maxWidth archetype={arch.archetype} />
      </div>
    );
  });

  const nonDiscoveredMap = leftOutArchs.map((l, i) => {
    return (
      <ArchetypeUnknown maxWidth noMargin key={i} locked={false} name={l} />
    );
  });
  return (
    <div className={styles.main}>
      <p style={{ flex: "0 1 auto" }} className={styles.archetypeText}>
        ARCHETYPE
      </p>
      <div
        style={{ marginTop: "30%", opacity: opacity.step1 }}
        className={styles.elementsWrapper}
      >
        <div className={styles.elementsDivNon}>
          {mapped}
          {nonDiscoveredMap}
        </div>
      </div>
      <div style={{ marginTop: "30%" }} className={styles.bottomStepOneDiv}>
        <p
          style={{ opacity: opacity.step2 }}
          className={styles.yourDiscoveredText}
        >
          There are 4 Archetypes to unlock on Dimensional:
        </p>
        <p style={{ opacity: opacity.step3 }} className={styles.descriptor}>
          Complete more Dimensions to unlock your other Archetypes.
        </p>
      </div>
    </div>
  );
}

export function ArchetypeDiscoveryScreenDeltaView(props: DeltaProps) {
  const opacity = useStaggeredOpacity(4);

  const oldArchetype = getElementFromTraitIdentifier(
    props.archetypeDeltaData?.previousTraitIdentifier
  ) as ExtendedArchetype | null;
  const newArchetype = getElementFromTraitIdentifier(
    props.archetypeDeltaData?.currentTraitIdentifier
  ) as ExtendedArchetype | null;

  const changedText =
    oldArchetype?.slug === newArchetype?.slug
      ? "is unchanged."
      : "has changed.";

  const longestLength = getLengthOfLongestArchetypeName(
    oldArchetype?.acronym ?? oldArchetype?.name,
    newArchetype?.acronym ?? newArchetype?.name
  );

  return (
    <div className={styles.main}>
      <p style={{ opacity: opacity.step1 }} className={styles.archetypeText}>
        ARCHETYPE
      </p>
      <div style={{ opacity: opacity.step2 }} className={styles.archDiv}>
        <div className={styles.architypeBody}>
          <p className={styles.topText}>
            {newArchetype && archetypeMapper[newArchetype?.dimensionSlug]}
          </p>

          <svg
            width="90"
            height="78"
            viewBox="0 0 90 78"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <clipPath id="ArchetypeImageClipPathLarge">
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M84.1172 77.6604C88.2137 77.6604 90.7568 73.1894 88.6744 69.6485L49.5568 3.1302C47.509 -0.351996 42.4902 -0.351994 40.4424 3.13021L1.32479 69.6485C-0.757529 73.1894 1.78558 77.6604 5.882 77.6604H84.1172ZM45.072 54.8056C48.6339 54.8056 51.5215 51.9073 51.5215 48.3319C51.5215 44.7566 48.6339 41.8582 45.072 41.8582C41.51 41.8582 38.6225 44.7566 38.6225 48.3319C38.6225 51.9073 41.51 54.8056 45.072 54.8056Z"
                fill="#FFFFF2"
              />
            </clipPath>
          </svg>

          <img
            alt="image"
            className="archetype-image-discovery"
            src={`/archetypes/${newArchetype?.slug}.jpg`}
            width="89px"
            height="89px"
          />
          <div className={styles.bottomTextDiv}>
            <p className={styles.bottomText}>
              {newArchetype?.acronym
                ? `${newArchetype.name} (${newArchetype.acronym})`
                : newArchetype?.name}
            </p>
          </div>
        </div>
      </div>
      <div style={{ opacity: opacity.step3 }} className={styles.deltaDiv}>
        <div className={styles.deltaInnerDiv}>
          <p
            style={{
              fontSize:
                longestLength <= 4
                  ? "40px"
                  : longestLength <= 8
                  ? "30px"
                  : "20px",
            }}
            className={styles.deltaArchName}
          >
            {oldArchetype?.acronym ?? oldArchetype?.name}
          </p>
          <p className={`${styles.beforeAfterText} ${font.className}`}>
            BEFORE
          </p>
        </div>
        <ForwardArrowDeltaScreen />
        <div className={styles.deltaInnerDiv}>
          <p
            style={{
              fontSize:
                longestLength <= 4
                  ? "40px"
                  : longestLength <= 8
                  ? "30px"
                  : "20px",
            }}
            className={styles.deltaArchName}
          >
            {newArchetype?.acronym ?? newArchetype?.name}
          </p>
          <p className={`${styles.beforeAfterText} ${font.className}`}>NOW</p>
        </div>
      </div>
      <div
        style={{ opacity: opacity.step4 }}
        className={styles.bottomStepOneDiv}
      >
        <p className={styles.yourDiscoveredText}>
          Your {newArchetype && archetypeMapper[newArchetype?.dimensionSlug]}{" "}
          Archetype {changedText}
        </p>
      </div>
    </div>
  );
}

export function ForwardArrowDeltaScreen(props: { marginTop?: number }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="50"
      height="50"
      viewBox="0 0 50 50"
      fill="none"
      style={{ marginTop: props.marginTop ? `${props.marginTop}px` : "0" }}
    >
      <path
        d="M44.6001 24.9966C44.5867 24.6464 44.4413 24.2406 44.202 23.986L36.362 15.656C35.8299 15.1794 34.8116 14.9823 34.2183 15.5335C33.6326 16.0771 33.6517 17.1317 34.2336 17.6773L39.7461 23.5269H6.87C6.05846 23.5269 5.4 24.1853 5.4 24.9969C5.4 25.8084 6.05846 26.4669 6.87 26.4669H39.7461L34.2336 32.3165C33.7321 32.8199 33.6383 33.9109 34.2183 34.4602C34.7963 35.0096 35.8528 34.8335 36.362 34.3377L44.202 26.0077C44.47 25.7225 44.6001 25.3876 44.6001 24.9971V24.9966Z"
        fill="#FFFFF2"
      />
    </svg>
  );
}
